import { DelayedOperationNotice } from '@workspace/ui/components/delayed-operation-notice';
import { Config } from '@/utils/config';
import { AppRoutes, isDocumentAgentPath } from '@/pages/routes-config';
import {
  AgentChatSkeleton,
  AgentHeroSkeleton,
  AgentSidebarSkeleton,
} from '@/pages/document-agent/agent-skeletons';
import { DOCUMENT_AGENT_SIDEBAR_STORAGE_KEY } from '@/pages/document-agent/constants';
import { defaultTrueBooleanAtomFamily, objectAtomFamily } from '@/jotai/atom';
import { atomKeys } from '@/jotai/atom-keys';
import HeaderShell from '@/components/generic/Header/header-shell';
import { Skeleton } from '@workspace/ui/components/skeleton';
import { cn } from '@workspace/ui/lib/utils';
import { useAtomValue } from 'jotai';
import { LucideGripVertical } from 'lucide-react';
import { memo, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

type SplashVariant = 'auto' | 'builder' | 'view' | 'agent' | 'default' | 'template';

export interface SplashScreenProps {
  variant?: SplashVariant;
  loading?: boolean;
}

const RIGHT_SIDEBAR_DEFAULT_WIDTH = 410;

const resolveVariant = (
  variant: SplashVariant,
  pathname: string,
): Exclude<SplashVariant, 'auto'> => {
  if (variant !== 'auto') return variant;

  if (isDocumentAgentPath(pathname)) {
    return 'agent';
  }

  if (pathname.startsWith('/project/edit/')) {
    return 'builder';
  }

  if (pathname.startsWith('/template-preview/')) {
    return 'template';
  }

  const isViewLikePath =
    pathname.startsWith('/project/view/') ||
    pathname.startsWith('/view/') ||
    pathname.startsWith('/project/preview/') ||
    pathname === AppRoutes.testPreview() ||
    pathname === AppRoutes.export();

  if (isViewLikePath) {
    return 'view';
  }

  return 'default';
};

const LoaderShell = ({
  children,
  className,
  loading = true,
}: {
  children: ReactNode;
  className?: string;
  loading?: boolean;
}) => (
  <main
    id="splash-screen"
    data-loading={loading ? 'true' : 'false'}
    className={cn(
      'fixed inset-0 z-50 h-dvh w-full overflow-hidden transition-opacity duration-900 delay-200',
      'data-[loading=false]:pointer-events-none data-[loading=false]:opacity-0',
      className,
    )}
  >
    {children}
    {loading && <DelayedOperationNotice delayMs={Config.DELAYED_OPERATION_NOTICE_MS} />}
  </main>
);

const getBuilderRightSidebarWidth = () => {
  if (typeof window === 'undefined') return RIGHT_SIDEBAR_DEFAULT_WIDTH;

  const stored = window.localStorage.getItem('builder-right-sidebar-width');
  if (stored === null) return RIGHT_SIDEBAR_DEFAULT_WIDTH;

  const parsed = Number(stored);
  return Number.isFinite(parsed)
    ? Math.min(650, Math.max(360, parsed))
    : RIGHT_SIDEBAR_DEFAULT_WIDTH;
};

const BuilderSkeleton = memo(({ loading = true }: { loading?: boolean }) => {
  const rightSidebarWidth = useMemo(() => getBuilderRightSidebarWidth(), []);

  return (
    <LoaderShell
      loading={loading}
      className="bg-linear-to-b from-white via-slate-50/50 to-blue-50/20"
    >
      <div className="flex h-full flex-col">
        <header className="relative flex items-center justify-between gap-3 px-2 py-2 sm:px-4 bg-white border-b border-gray-65">
          <div className="shrink-0 flex items-center gap-1 sm:gap-1.5 md:gap-2">
            <Skeleton className="size-8 rounded-md bg-gray-60" />
            <Skeleton className="h-7 w-32 rounded-md bg-gray-60" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Skeleton className="max-lg:hidden h-8 w-20 rounded-md bg-gray-60" />
            <Skeleton className="h-8 w-20 rounded-md bg-gray-60" />
            <Skeleton className="h-8 w-8 rounded-md bg-gray-60" />
          </div>
        </header>

        {/* <div className="flex min-h-0 flex-1">
          <aside className="hidden lg:block h-64 w-10 ms-3 my-3 p-1 rounded-md bg-white space-y-1">
            <Skeleton className="size-8 bg-gray-60 rounded-md" />
            <Skeleton className="size-8 bg-gray-60 rounded-md" />
            <Skeleton className="size-8 bg-gray-60 rounded-md" />
            <Skeleton className="size-8 bg-gray-60 rounded-md" />
            <Skeleton className="size-8 bg-gray-60 rounded-md" />
            <Skeleton className="size-8 bg-gray-60 rounded-md" />
            <Skeleton className="size-8 bg-gray-60 rounded-md" />
          </aside>

          <section className="min-w-0 flex-1 overflow-hidden px-2 sm:px-4 py-12">
            <div className="mx-auto h-full max-w-245 space-y-4">
              <Skeleton className="h-133 w-full rounded-2xl bg-gray-60" />
              <Skeleton className="h-133 w-full rounded-2xl bg-gray-60" />
            </div>
          </section>

          <aside
            className="hidden xl:flex h-full shrink-0 bg-white"
            style={{ width: `${rightSidebarWidth}px` }}
          >
            <div className="relative flex flex-col flex-1 min-w-0 h-full">
              <div className="flex min-h-11 items-center justify-between gap-2 border-b border-gray-65 px-3 py-2">
                <Skeleton className="h-5 w-2/4 rounded-md bg-gray-60" />
                <Skeleton className="size-5 rounded-md bg-gray-60" />
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-3">
                <div className="flex flex-col gap-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="flex items-start gap-1">
                      <LucideGripVertical className="size-3.5 mt-3 shrink-0 text-gray-30 animate-pulse" />
                      <Skeleton className="h-18 w-full rounded-lg bg-gray-60" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex h-full w-12 shrink-0 flex-col items-center gap-1 border-l border-gray-65 bg-white px-1.5 py-1.5">
              {[1, 2, 3, 5, 6, 7, 8, 9].map((i) => (
                <Skeleton key={i} className="size-8 rounded-md bg-gray-60" />
              ))}
            </div>
          </aside>
        </div> */}
        <div className="flex h-full flex-col items-center justify-center">
          <FileWaveLoader />
        </div>
      </div>
    </LoaderShell>
  );
});

const fileWaveKeyframes = `
  @keyframes doc-float {
    0%, 100% { transform: translateY(0) rotate(-1deg); }
    50% { transform: translateY(-8px) rotate(1deg); }
  }
  @keyframes line-draw {
    0% { transform: scaleX(0); opacity: 0; }
    15% { opacity: 1; }
    55% { transform: scaleX(1); opacity: 1; }
    80%, 100% { transform: scaleX(1); opacity: 1; }
  }
  @keyframes pencil-write {
    0%, 100% { transform: translate(0, 0) rotate(-12deg); }
    25% { transform: translate(42px, 10px) rotate(-12deg); }
    50% { transform: translate(0, 20px) rotate(-12deg); }
    75% { transform: translate(42px, 30px) rotate(-12deg); }
  }
  @keyframes pop-in {
    0%, 55% { transform: scale(0); opacity: 0; }
    70% { transform: scale(1.15); opacity: 1; }
    85%, 100% { transform: scale(1); opacity: 1; }
  }
  @keyframes sparkle-float {
    0%, 100% { transform: translateY(0) scale(1); opacity: 0.7; }
    50% { transform: translateY(-10px) scale(1.2); opacity: 1; }
  }
  @keyframes dot-bounce {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
    40% { transform: translateY(-5px); opacity: 1; }
  }
  @keyframes blob-pulse {
    0%, 100% { transform: scale(1); opacity: 0.6; }
    50% { transform: scale(1.08); opacity: 0.9; }
  }
  @keyframes message-in {
    0% { transform: translateY(4px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
`;

const LOADING_MESSAGES = [
  'Getting things ready',
  'Setting things up',
  'Loading your workspace',
  'Almost there',
  'Just a moment',
];
const LOADING_QUOTES = [
  'Well begun is half done.',
  'Quality is never an accident.',
  'Simplicity is the ultimate sophistication.',
  'Small steps every day lead to big results.',
  'Great things take a little time.',
  'Focus on progress, not perfection.',
  'Success is the sum of small efforts, repeated daily.',
  'The secret of getting ahead is getting started.',
  'Opportunities don’t happen, you create them.',
  'Dream big, start small, act now.',
  'Make each day your masterpiece.',
  'Believe you can and you’re halfway there.',
  'Hard work beats talent when talent doesn’t work hard.',
  'Your work is your signature.',
  'Details make perfection, and perfection is no detail.',
  'Trust is built one promise at a time.',
  'Good work speaks for itself.',
  'Clarity is the first step to every yes.',
  'Every great deal starts with a great idea.',
  'Done is better than perfect.',
  'Patience and persistence win the race.',
  'Keep going, good things are on the way.',
];
const LOADING_MESSAGE_INTERVAL_MS = 1000;
const LOADING_QUOTE_INTERVAL_MS = 3000;

const shuffle = (items: string[]) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const LoadingMessage = memo(({ quotes = false }: { quotes?: boolean }) => {
  const messages = useMemo(() => (quotes ? shuffle(LOADING_QUOTES) : LOADING_MESSAGES), [quotes]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % messages.length),
      quotes ? LOADING_QUOTE_INTERVAL_MS : LOADING_MESSAGE_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [messages, quotes]);

  if (quotes) {
    return (
      <p
        key={index}
        className="max-w-xs px-4 text-center text-sm italic text-gray-500 animate-[message-in_0.4s_ease-out]"
      >
        “{messages[index]}”
      </p>
    );
  }

  return (
    <p
      key={index}
      className="ml-1 text-sm text-gray-500 animate-[message-in_0.4s_ease-out]"
    >
      {messages[index]}
    </p>
  );
});

export const FileWaveLoader = memo(({ quotes = false }: { quotes?: boolean }) => {
  return (
    <div
      role="status"
      aria-label="Loading document"
      className="flex flex-col items-center justify-center gap-5 select-none"
    >
      <style>{fileWaveKeyframes}</style>

      <div className="relative h-60 w-64 sm:w-72">
        <div className="absolute left-1/2 top-1/2 size-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-50 blur-2xl animate-[blob-pulse_4s_ease-in-out_infinite]" />

        <div className="absolute left-1/2 top-6 h-44 w-36 -translate-x-1/2 rotate-6 rounded-2xl bg-slate-100" />
        <div className="absolute left-1/2 top-6 h-44 w-36 -translate-x-1/2 -rotate-6 rounded-2xl bg-indigo-50" />

        <div className="absolute left-1/2 top-4 h-48 w-40 -translate-x-1/2 animate-[doc-float_3.5s_ease-in-out_infinite]">
          <div className="relative h-full w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.10)]">
            <div className="absolute right-0 top-0 size-8 rounded-bl-2xl bg-blue-50" />
            <div
              className="absolute right-0 top-0 size-8 rounded-bl-2xl rounded-tr-2xl bg-slate-100"
              style={{ clipPath: 'polygon(0 0, 100% 100%, 0 100%)' }}
            />

            <div className="px-4 pb-4 pt-4">
              <div className="flex items-center gap-2">
                <div className="size-6 rounded-lg bg-indigo-100" />
                <div className="h-2 w-16 origin-left rounded-full bg-slate-200 animate-[line-draw_2.4s_ease-in-out_infinite]" />
              </div>
              <div className="mt-3 space-y-2">
                <div className="h-2.5 w-11/12 origin-left rounded-full bg-slate-200 animate-[line-draw_2.4s_ease-in-out_0.1s_infinite]" />
                <div className="h-2.5 w-3/4 origin-left rounded-full bg-slate-100 animate-[line-draw_2.4s_ease-in-out_0.2s_infinite]" />
                <div className="h-2.5 w-4/5 origin-left rounded-full bg-slate-100 animate-[line-draw_2.4s_ease-in-out_0.3s_infinite]" />
              </div>

              <div className="relative mt-3 h-20 overflow-hidden rounded-xl bg-gradient-to-b from-blue-50 to-indigo-50">
                <div className="absolute left-4 top-3 size-5 rounded-full bg-amber-100" />
                <div
                  className="absolute bottom-0 left-0 right-0 h-10 bg-emerald-50"
                  style={{ clipPath: 'polygon(0 100%, 25% 30%, 45% 70%, 65% 20%, 100% 100%)' }}
                />
              </div>

              <div className="mt-3 flex gap-1.5">
                <div className="h-5 w-12 rounded-full bg-indigo-50" />
                <div className="h-5 w-12 rounded-full bg-emerald-50" />
              </div>
            </div>

            <div className="absolute -right-1 bottom-10 size-10 rounded-full bg-emerald-100 opacity-0 transform-[scale(0)] ring-4 ring-white animate-[pop-in_2.4s_ease-in-out_infinite]">
              <svg viewBox="0 0 24 24" className="size-full p-2" fill="none">
                <path
                  d="M6 12.5l4 4L18 8"
                  stroke="#6b9e8a"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className="absolute -right-3 top-10 animate-[pencil-write_2.4s_ease-in-out_infinite]">
            <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
              <rect
                x="12"
                y="2"
                width="9"
                height="20"
                rx="2"
                transform="rotate(24 16 12)"
                fill="#e8ddc9"
              />
              <path d="M19.5 21.5l3.8 1.6-1.2 4-4.5-2.4z" fill="#c9bfae" />
              <path d="M17.6 24.7l2.4 1-0.7 2.4-2.7-1.4z" fill="#64748b" />
            </svg>
          </div>
        </div>

        <div className="absolute left-4 top-10 size-2 rounded-full bg-amber-200 animate-[sparkle-float_2.6s_ease-in-out_infinite]" />
        <div className="absolute right-6 top-6 animate-[sparkle-float_2.6s_ease-in-out_0.4s_infinite]">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 0l1.4 5.6L14 7l-5.6 1.4L7 14 5.6 8.4 0 7l5.6-1.4z" fill="#cbd5e1" />
          </svg>
        </div>
        <div className="absolute bottom-8 left-8 size-1.5 rounded-full bg-indigo-200 animate-[sparkle-float_2.6s_ease-in-out_0.8s_infinite]" />
        <div className="absolute bottom-12 right-4 size-2.5 rounded-full bg-emerald-100 animate-[sparkle-float_2.6s_ease-in-out_1.1s_infinite]" />
      </div>

      <div className="flex items-center gap-2">
        <span className="size-1.5 rounded-full bg-slate-300 animate-[dot-bounce_1.2s_ease-in-out_infinite]" />
        <span className="size-1.5 rounded-full bg-slate-300 animate-[dot-bounce_1.2s_ease-in-out_0.15s_infinite]" />
        <span className="size-1.5 rounded-full bg-slate-300 animate-[dot-bounce_1.2s_ease-in-out_0.3s_infinite]" />
        {!quotes && <LoadingMessage />}
      </div>
      {quotes && <LoadingMessage quotes />}
    </div>
  );
});

const ViewSkeleton = memo(({ loading = true }: { loading?: boolean }) => {
  const projectInfo = useAtomValue(objectAtomFamily(atomKeys.projectInfo));
  const logoUrl = projectInfo?.organization?.logo || null;
  const title = projectInfo?.title;

  return (
    <LoaderShell loading={loading} className="bg-white">
      <div className="flex h-full flex-col bg-white">
        <HeaderShell className="fixed top-0 left-0 right-0 z-30">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
              {logoUrl && (
                <>
                  <div className="flex shrink-0 items-center h-7">
                    <img
                      src={logoUrl || '/images/logo.png'}
                      alt="Logo"
                      className="h-full w-auto object-contain"
                    />
                  </div>
                  <div className="h-4 w-px shrink-0 bg-gray-30/30" />
                </>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-primary-darker sm:text-base">
                  {title}
                </p>
              </div>
            </div>
            <div className="w-full flex flex-wrap items-center justify-end gap-2 sm:w-auto sm:flex-nowrap">
              <Skeleton className="size-8 rounded-md" />
              <Skeleton className="h-8 w-24 rounded-md max-lg:w-8" />
            </div>
          </div>
        </HeaderShell>

        <div className="flex min-h-0 flex-1 items-center justify-center p-6 bg-linear-to-b from-white via-slate-50/50 to-blue-50/20 pt-(--header-height,60px)">
          <FileWaveLoader quotes />
        </div>
      </div>
    </LoaderShell>
  );
});

// The page shows the sidebar only when past chats exist, open unless the user collapsed it. A chat
// link or a saved toggle (only possible once the sidebar was shown) means past chats exist; null
// means no sidebar.
const getAgentSidebarState = (hasChat: boolean): boolean | null => {
  try {
    const stored = window.localStorage.getItem(DOCUMENT_AGENT_SIDEBAR_STORAGE_KEY);
    if (stored !== null) return stored === 'true';
  } catch {
    // Storage unavailable: fall back to the chat link.
  }
  return hasChat ? true : null;
};

// Mirrors the document agent page: a chat link shows a conversation, anything else the new chat.
const AgentSkeleton = memo(({ loading = true, hasChat }: { loading?: boolean; hasChat: boolean }) => {
  const isGuest = Config.APP_RUN_TYPE === 'guest';
  const sidebarState = useMemo(() => getAgentSidebarState(hasChat), [hasChat]);

  return (
    <LoaderShell loading={loading} className="bg-white">
      <div className="flex flex-col w-full h-full">
        <header className="flex justify-between items-center gap-2 bg-white px-4 sm:px-6 py-2.5 border-border border-b w-full">
          <div className="h-7 sm:h-8 shrink-0">
            <img
              src="/images/logo.png"
              alt="logo"
              height={400}
              width={1000}
              className="w-auto h-full max-h-7 sm:max-h-8"
              fetchPriority="high"
            />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="bg-purple-50 rounded-lg size-8" />
            <Skeleton className="bg-purple-50 rounded-lg size-8" />
          </div>
        </header>

        <div className="flex flex-1 min-h-0">
          {!isGuest && sidebarState !== null && <AgentSidebarSkeleton isOpen={sidebarState} />}
          <div className="flex flex-col flex-1 px-4 pb-4 sm:pb-6 min-w-0 min-h-0">
            {hasChat ? <AgentChatSkeleton /> : <AgentHeroSkeleton showStartOptions={!isGuest} />}
          </div>
        </div>
      </div>
    </LoaderShell>
  );
});

const TemplateSkeleton = memo(({ loading = true }: { loading?: boolean }) => (
  <LoaderShell
    loading={loading}
    className="bg-linear-to-b from-white via-slate-50/50 to-blue-50/20"
  >
    <div className="flex h-full flex-col items-center justify-center">
      {/* <section className="min-w-0 flex-1 overflow-hidden px-2 sm:px-4 py-12">
        <div className="mx-auto h-full max-w-245 space-y-4">
          <Skeleton className="h-133 w-full rounded-2xl bg-gray-60" />
          <Skeleton className="h-133 w-full rounded-2xl bg-gray-60" />
          <Skeleton className="h-133 w-full rounded-2xl bg-gray-60" />
        </div>
        </section> */}
      <FileWaveLoader quotes />
    </div>
  </LoaderShell>
));

const DefaultSkeleton = memo(({ loading = true }: { loading?: boolean }) => (
  <LoaderShell loading={loading}>
    <div className="flex items-center justify-center absolute inset-0 animate-pulse">
      <div
        style={{
          width: '142px',
          height: '40px',
          backgroundImage: 'var(--splash-logo-url, url(/images/loader.svg))',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      />
    </div>
  </LoaderShell>
));

const SplashScreen = ({ variant = 'auto', loading: propLoading }: SplashScreenProps) => {
  const location = useLocation();
  const pathname = variant === 'auto' ? location.pathname : '';
  const headerLoading = useAtomValue(defaultTrueBooleanAtomFamily(atomKeys.headerLoading));

  const isExcluded =
    pathname === AppRoutes.proposalNotFound() || pathname === AppRoutes.figmaServicesPreview();

  const loading = propLoading !== undefined ? propLoading : headerLoading && !isExcluded;
  // console.log('SplashScreen', { variant, loading });
  const resolvedVariant = useMemo(() => resolveVariant(variant, pathname), [variant, pathname]);

  if (resolvedVariant === 'builder') return <BuilderSkeleton loading={loading} />;
  if (resolvedVariant === 'view') return <ViewSkeleton loading={loading} />;
  if (resolvedVariant === 'agent') {
    const hasChat = Boolean(new URLSearchParams(location.search).get('chatId')?.trim());
    return <AgentSkeleton loading={loading} hasChat={hasChat} />;
  }
  if (resolvedVariant === 'template') return <TemplateSkeleton loading={loading} />;

  return <DefaultSkeleton loading={loading} />;
};

export default memo(SplashScreen);
